"use client"

import { useState, useEffect, useCallback } from "react"
import type { ConnectionStatus } from "../types/field-mapping"
import { FIELD_MAPPING_CONSTANTS } from "../constants/field-mapping"

export const useConnectionStatus = (successOAuth?: boolean) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    hubspot: { connected: false, tenantId: null, error: null },
    salesforce: { connected: false, tenantId: null, error: null },
  })

  const checkConnectionStatus = useCallback(() => {
    const hubspotConnected = localStorage.getItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.HUBSPOT_CONNECTED) === "true"
    const salesforceConnected =
      localStorage.getItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.SALESFORCE_CONNECTED) === "true"
    const hubspotTenantId = localStorage.getItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.HUBSPOT_TENANT_ID)
    const salesforceTenantId = localStorage.getItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.SALESFORCE_TENANT_ID)

    setConnectionStatus({
      hubspot: {
        connected: hubspotConnected,
        tenantId: hubspotTenantId,
        error: null,
      },
      salesforce: {
        connected: salesforceConnected,
        tenantId: salesforceTenantId,
        error: null,
      },
    })
  }, [])

  const updateConnectionError = useCallback((provider: "hubspot" | "salesforce", error: string) => {
    setConnectionStatus((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], error },
    }))
  }, [])

  const clearConnectionError = useCallback((provider: "hubspot" | "salesforce") => {
    setConnectionStatus((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], error: null },
    }))
  }, [])

  const disconnect = useCallback((provider: "hubspot" | "salesforce") => {
    localStorage.removeItem(`${provider}_connected`)
    localStorage.removeItem(`${provider}_tenant_id`)

    if (provider === "hubspot") {
      // Also disconnect Salesforce if HubSpot is disconnected
      localStorage.removeItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.SALESFORCE_CONNECTED)
      localStorage.removeItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.SALESFORCE_TENANT_ID)

      setConnectionStatus({
        hubspot: { connected: false, tenantId: null, error: null },
        salesforce: { connected: false, tenantId: null, error: null },
      })
    } else {
      setConnectionStatus((prev) => ({
        ...prev,
        salesforce: { connected: false, tenantId: null, error: null },
      }))
    }
  }, [])

  useEffect(() => {
    checkConnectionStatus()
  }, [checkConnectionStatus, successOAuth])

  return {
    connectionStatus,
    updateConnectionError,
    clearConnectionError,
    disconnect,
    checkConnectionStatus,
  }
}
